/**
 * FocusSwitch.jsx — Focus / Full toggle for the sidebar footer and the mobile More sheet.
 * Renders nothing for someone without a Focus set: there's nothing to switch.
 */
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { CenterFocusStrong as FocusIcon, Apps as FullIcon } from '@mui/icons-material';
import { useFocus, useSetFocusMode } from '../../hooks/useFocus';

export default function FocusSwitch({ compact = false, tone = 'dark' }) {
  const { mode, hiddenCount } = useFocus();
  const setMode = useSetFocusMode();
  if (!hiddenCount) return null;

  const isFocus = mode === 'focus';
  const next = isFocus ? 'full' : 'focus';
  const hint = isFocus
    ? `Focus: ${hiddenCount} rarely used item${hiddenCount === 1 ? '' : 's'} hidden. Click for Full view.`
    : 'Full view: everything shown. Click to return to Focus.';

  if (compact) {
    return (
      <Tooltip title={hint} placement="right" arrow>
        <IconButton
          data-track={`focus.switch.${next}`}
          aria-label={hint}
          onClick={() => setMode.mutate(next)}
          sx={{ display: 'flex', mx: 'auto', color: isFocus ? '#13B5EA' : 'rgba(255,255,255,0.5)' }}
        >
          {isFocus ? <FocusIcon fontSize="small" /> : <FullIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    );
  }

  const dark = tone === 'dark';
  const segment = (value, label) => {
    const on = mode === value;
    return (
      <Box
        component="button"
        type="button"
        data-track={`focus.switch.${value}`}
        aria-pressed={on}
        onClick={() => !on && setMode.mutate(value)}
        sx={{
          flex: 1, py: 0.5, border: 0, borderRadius: 0.75, cursor: on ? 'default' : 'pointer',
          fontSize: '0.75rem', fontWeight: on ? 600 : 400, fontFamily: 'inherit',
          bgcolor: on ? (dark ? 'rgba(19, 181, 234, 0.25)' : 'primary.main') : 'transparent',
          color: on ? (dark ? 'white' : 'primary.contrastText') : (dark ? 'rgba(255,255,255,0.55)' : 'text.secondary'),
          '&:hover': on ? {} : { color: dark ? 'white' : 'text.primary' },
        }}
      >
        {label}
      </Box>
    );
  };

  return (
    <Box sx={{ px: 1, pb: 0.75 }}>
      <Box
        role="group"
        aria-label="View"
        sx={{
          display: 'flex', gap: 0.5, p: 0.25, borderRadius: 1,
          border: 1, borderColor: dark ? 'rgba(255,255,255,0.12)' : 'divider',
        }}
      >
        {segment('focus', 'Focus')}
        {segment('full', 'Full')}
      </Box>
      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 0.5, px: 0.25, fontSize: '0.65rem', color: dark ? 'rgba(255,255,255,0.45)' : 'text.secondary' }}
      >
        {isFocus ? `${hiddenCount} rarely used hidden` : 'Showing everything'}
      </Typography>
    </Box>
  );
}
